import { hydrateStepTags } from './tags.js';
import {
  googleSheetFields,
  isGoogleSheetStep,
  sheetFoundNextOrder,
  sheetNotFoundNextOrder,
} from './googleSheetStep.js';
import {
  isWindexStep,
  windexFields,
  windexNoResultNextOrder,
} from './windexStep.js';
import { withStageId } from './pipelineStage.js';

export const FLOW_JSON_VERSION = 1;

const IDENTITY_KEYS = [
  'id',
  'created_at',
  'updated_at',
  'flow',
  'flow_id',
  'step',
  'step_id',
];

const SECRET_KEY = /token|password|secret|authorization|api_key|credential/i;

export class FlowJsonError extends Error {
  constructor(message) {
    super(message);
    this.name = 'FlowJsonError';
  }
}

function fail(message) {
  throw new FlowJsonError(message);
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function stripIdentity(entity) {
  const copy = clone(entity);
  IDENTITY_KEYS.forEach((key) => {
    delete copy[key];
  });
  Object.keys(copy).forEach((key) => {
    if (SECRET_KEY.test(key)) delete copy[key];
  });
  return copy;
}

function nodeIdFor(step) {
  return step.node_id || `step_${step.order}`;
}

function keywordsOf(raw) {
  // Explicit "" must win. `||` would restore the old singular keyword.
  const value =
    typeof raw.keywords === 'string'
      ? raw.keywords
      : (raw.keywords ?? raw.keyword ?? raw.trigger_keywords ?? '');
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string'
          ? item.trim()
          : String(item?.name || item?.keyword || item?.value || ''),
      )
      .filter(Boolean)
      .join(', ');
  }
  return typeof value === 'string' ? value : '';
}

function portableStep(step) {
  let copy = stripIdentity(step);
  delete copy.google_sheet_file_name;
  copy = withStageId(copy);
  copy.options = Array.isArray(copy.options)
    ? copy.options.map((opt) => withStageId(stripIdentity(opt)))
    : [];

  if (copy.is_quotation_step || copy.is_datetime_step) {
    return hydrateStepTags({
      ...copy,
      ...googleSheetFields({ is_google_sheet_step: false }),
      ...windexFields({ is_windex_step: false }),
    });
  }
  if (isWindexStep(copy)) {
    const fields = windexFields(copy);
    const payload = {
      ...copy,
      options: [],
      is_open_ended: false,
      is_confirmation_step: false,
      is_datetime_step: false,
      is_quotation_step: false,
      quotation_id: null,
      ...googleSheetFields({ is_google_sheet_step: false }),
      ...fields,
    };
    if (!fields.windex_field) delete payload.windex_field;
    return hydrateStepTags(payload);
  }
  if (isGoogleSheetStep(copy)) {
    return hydrateStepTags({
      ...copy,
      options: [],
      is_open_ended: false,
      is_confirmation_step: false,
      is_datetime_step: false,
      is_quotation_step: false,
      quotation_id: null,
      ...googleSheetFields(copy),
      ...windexFields({ is_windex_step: false }),
    });
  }
  return hydrateStepTags(copy);
}

function positionOf(node) {
  const x = node?.position?.x;
  const y = node?.position?.y;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function fallbackPosition(index) {
  return { x: 80 + (index % 4) * 380, y: 80 + Math.floor(index / 4) * 220 };
}

function stepsFromNodes(nodes) {
  return nodes
    .filter((node) => node.id !== 'start')
    .map((node) => {
      if (!node.data?.fullStep) {
        fail(`Node "${node.id}" has no step configuration.`);
      }
      return {
        ...node.data.fullStep,
        node_id: node.data.fullStep.node_id || node.id,
      };
    });
}

function routeSignature(step) {
  return JSON.stringify({
    next: step.next_step_order ?? null,
    found: step.google_sheet_found_next_step_order ?? null,
    notFound: step.google_sheet_not_found_next_step_order ?? null,
    noMatch: step.google_sheet_no_match_next_step_order ?? null,
    windex: step.windex_no_result_next_step_order ?? null,
    question: step.question_text ?? '',
    variable: step.variable_name ?? '',
    options: (step.options || []).map((opt) => [
      opt.number ?? null,
      opt.text ?? '',
      opt.next_step_order ?? null,
    ]),
  });
}

function assertStepsMatchNodes(steps, nodes) {
  const fromNodes = stepsFromNodes(nodes);
  if (fromNodes.length !== steps.length) {
    fail('Flow JSON steps and nodes describe different steps.');
  }
  const byOrder = new Map(fromNodes.map((step) => [Number(step.order), step]));
  steps.forEach((step) => {
    const other = byOrder.get(Number(step.order));
    if (!other || routeSignature(step) !== routeSignature(other)) {
      fail('Flow JSON steps and nodes conflict. Import cancelled.');
    }
  });
}

function assertEdgesMatch(steps, edges) {
  if (!Array.isArray(edges)) return;
  const expected = new Set();
  const first = steps.find((step) => Number(step.order) === 1) || steps[0];
  if (first) {
    expected.add(`start|main_out|${nodeIdFor(first)}`);
  }
  steps.forEach((step) => {
    const source = nodeIdFor(step);
    const add = (handle, order) => {
      if (order == null || order === '') return;
      const target = steps.find((item) => Number(item.order) === Number(order));
      if (!target) return;
      expected.add(`${source}|${handle}|${nodeIdFor(target)}`);
    };
    if (isGoogleSheetStep(step)) {
      add('sheet_found', sheetFoundNextOrder(step));
      add('sheet_not_found', sheetNotFoundNextOrder(step));
    } else if (isWindexStep(step)) {
      add('windex_found', step.next_step_order);
      add('windex_no_result', windexNoResultNextOrder(step));
    } else if (Array.isArray(step.options) && step.options.length > 0) {
      step.options.forEach((opt) =>
        add(`opt_${opt.number}`, opt.next_step_order),
      );
    } else {
      add('main_out', step.next_step_order);
    }
  });
  const actual = new Set(
    edges.map(
      (edge) => `${edge.source}|${edge.sourceHandle || ''}|${edge.target}`,
    ),
  );
  if (actual.size !== expected.size) {
    fail('Flow JSON edges do not match step connections.');
  }
  expected.forEach((key) => {
    if (!actual.has(key))
      fail('Flow JSON edges do not match step connections.');
  });
}

function assertReferences(steps) {
  const orders = new Set();
  steps.forEach((step) => {
    const order = Number(step.order);
    if (!Number.isFinite(order)) fail('A step is missing a numeric order.');
    if (orders.has(order)) fail(`Duplicate step order ${order}.`);
    orders.add(order);
  });
  const ids = new Set();
  steps.forEach((step) => {
    const id = nodeIdFor(step);
    if (ids.has(id) || id === 'start') fail(`Duplicate node id "${id}".`);
    ids.add(id);
  });
  const check = (order, label) => {
    if (order == null || order === '') return;
    if (!orders.has(Number(order))) {
      fail(`${label} points at missing step ${order}.`);
    }
  };
  steps.forEach((step) => {
    check(step.next_step_order, `Step ${step.order}`);
    check(step.google_sheet_found_next_step_order, `Step ${step.order}`);
    check(step.google_sheet_not_found_next_step_order, `Step ${step.order}`);
    check(step.google_sheet_no_match_next_step_order, `Step ${step.order}`);
    check(step.windex_no_result_next_step_order, `Step ${step.order}`);
    (step.options || []).forEach((opt, index) => {
      check(opt.next_step_order, `Step ${step.order} option ${index + 1}`);
    });
  });
}

/**
 * Versioned frontend document. Steps are the flow. Positions are layout.
 * Edges are derived on restore, so they are not stored.
 */
export function serializeFlow({
  name,
  keywords,
  welcome_message,
  completion_message,
  steps,
  positions,
}) {
  const portableSteps = (steps || []).map(portableStep);
  assertReferences(portableSteps);
  const storedPositions = {};
  portableSteps.forEach((step) => {
    const id = nodeIdFor(step);
    const pos = positions?.[id];
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
      storedPositions[id] = { x: pos.x, y: pos.y };
    }
  });
  if (positions?.start && Number.isFinite(positions.start.x)) {
    storedPositions.start = { x: positions.start.x, y: positions.start.y };
  }
  return {
    schema_version: FLOW_JSON_VERSION,
    name: String(name || '').trim() || 'flow',
    keywords:
      typeof keywords === 'string' ? keywords : keywordsOf({ keywords }),
    welcome_message: welcome_message || '',
    completion_message: completion_message || '',
    steps: portableSteps,
    positions: storedPositions,
  };
}

export function serializeCanvasFlow(flow, nodes) {
  const positions = {};
  (nodes || []).forEach((node) => {
    const pos = positionOf(node);
    if (pos) positions[node.id] = pos;
  });
  return serializeFlow({
    name: flow?.name,
    keywords: keywordsOf(flow || {}),
    welcome_message: flow?.welcome_message,
    completion_message: flow?.completion_message,
    steps: flow?.steps || [],
    positions,
  });
}

export function isCompleteFlowDetail(detail) {
  return !!detail && Array.isArray(detail.steps);
}

export function serializeSavedFlow(detail) {
  if (!isCompleteFlowDetail(detail)) {
    fail('Complete flow data is unavailable.');
  }
  return serializeFlow({
    name: detail.name,
    keywords: keywordsOf(detail),
    welcome_message: detail.welcome_message,
    completion_message: detail.completion_message,
    steps: detail.steps,
    positions: detail.positions,
  });
}

export function parseFlowDocument(input) {
  let raw = input;
  if (typeof input === 'string') {
    try {
      raw = JSON.parse(input);
    } catch (err) {
      fail(`Invalid JSON format: ${err.message}`);
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    fail('Invalid JSON format: expected a flow object.');
  }
  if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.steps)) {
    raw = raw.data;
  }
  if (raw.schema_version != null && raw.schema_version !== FLOW_JSON_VERSION) {
    fail(`Unsupported flow JSON version ${raw.schema_version}.`);
  }

  const nodes = Array.isArray(raw.nodes) ? raw.nodes : null;
  if (nodes) {
    nodes.forEach((node) => {
      if (node.parentId || node.parentNode)
        fail('Grouped nodes are not supported.');
      if (node.type && node.type !== 'custom') {
        fail(`Unsupported node type "${node.type}".`);
      }
    });
  }
  let steps = Array.isArray(raw.steps) ? raw.steps : null;
  if (nodes && steps) assertStepsMatchNodes(steps, nodes);
  if (!steps) {
    if (!nodes) fail("Flow JSON must contain 'steps' or 'nodes'.");
    steps = stepsFromNodes(nodes);
  }

  const portableSteps = steps.map(portableStep);
  assertReferences(portableSteps);
  if (Array.isArray(raw.edges)) assertEdgesMatch(portableSteps, raw.edges);

  const positions = {
    ...(raw.positions && typeof raw.positions === 'object'
      ? raw.positions
      : {}),
  };
  if (nodes) {
    nodes.forEach((node) => {
      const pos = positionOf(node);
      if (node.position && !pos)
        fail(`Node "${node.id}" has an invalid position.`);
      if (pos && !positions[node.id]) positions[node.id] = pos;
    });
  }
  Object.entries(positions).forEach(([id, pos]) => {
    if (
      !pos ||
      !Number.isFinite(Number(pos.x)) ||
      !Number.isFinite(Number(pos.y))
    ) {
      fail(`Node "${id}" has an invalid position.`);
    }
    positions[id] = { x: Number(pos.x), y: Number(pos.y) };
  });

  const ordered = [...portableSteps].sort(
    (a, b) => Number(a.order) - Number(b.order),
  );
  if (!positions.start) positions.start = fallbackPosition(0);
  ordered.forEach((step, index) => {
    const id = nodeIdFor(step);
    if (!positions[id]) positions[id] = fallbackPosition(index + 1);
  });

  return {
    flow: {
      name: String(raw.name || '').trim() || 'Imported Flow',
      keywords: keywordsOf(raw),
      welcome_message: raw.welcome_message || '',
      completion_message: raw.completion_message || '',
      steps: portableSteps,
    },
    positions,
  };
}
